/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { TextViewProps } from '@airasia-phoenix/react-native/components/TextView';
import { openInAppBrowser } from '@airasia-rn/ui/utils';
import { TFunction } from 'i18next';
import type { OpenUrl } from '../hooks/useOpenUrl';

import { isEmpty } from './stringUtils';

export const CHINA_GEO_ID = 'CN'; // app geoId for China market

export const isChinaGeoId = (geoId?: string): boolean =>
  (geoId ?? '').toUpperCase() === CHINA_GEO_ID;

export const GENERAL_TERM_URLS = {
  TERMS_AND_CONDITIONS:
    'https://www.airasia.com/aa/about-us/en/gb/terms-and-conditions.html',
  TERMS_OF_USE: 'https://www.airasia.com/aa/about-us/en/gb/terms-of-use.html',
  PRIVACY_STATEMENT:
    'https://www.airasia.com/aa/about-us/en/gb/privacy-statement.html',
  GOOGLE_TERMS_OF_SERVICE: 'https://policies.google.com/terms',
  GOOGLE_PRIVACY_POLICY: 'https://policies.google.com/privacy',
} as const;

/** A spannable segment tagged with the URL it should open. */
export type DisclaimerSpannable = NonNullable<
  TextViewProps['spannableTextsProps']
>[number] & { url?: string };

export type DisclaimerProps = Omit<TextViewProps, 'spannableTextsProps'> & {
  spannableTextsProps?: DisclaimerSpannable[];
};

export type DisclaimerUrls = {
  termsAndConditions?: string;
  termsOfUse?: string;
  privacyStatement?: string;
  locale?: string;
};

export type DisclaimerLabels = Record<
  | 'Terms and conditions'
  | 'Terms of Use'
  | 'Privacy Statement'
  | 'Terms of Service'
  | 'Privacy Policy',
  string
>;

export const buildDisclaimerSpans = (
  disclaimer: DisclaimerProps,
  labels: DisclaimerLabels,
  urls: DisclaimerUrls = {},
): DisclaimerProps => {
  const googleHl = (urls.locale ?? 'en-gb').split('-')[0] || 'en';
  const segmentUrls: Record<keyof DisclaimerLabels, string> = {
    'Terms and conditions':
      urls.termsAndConditions ?? GENERAL_TERM_URLS.TERMS_AND_CONDITIONS,
    'Terms of Use': urls.termsOfUse ?? GENERAL_TERM_URLS.TERMS_OF_USE,
    'Privacy Statement':
      urls.privacyStatement ?? GENERAL_TERM_URLS.PRIVACY_STATEMENT,
    'Terms of Service': `${GENERAL_TERM_URLS.GOOGLE_TERMS_OF_SERVICE}?hl=${encodeURIComponent(googleHl)}`,
    'Privacy Policy': `${GENERAL_TERM_URLS.GOOGLE_PRIVACY_POLICY}?hl=${encodeURIComponent(googleHl)}`,
  };

  return {
    ...disclaimer,
    spannableTextsProps: disclaimer.spannableTextsProps?.map((item) => {
      const segment = item.spannable as keyof DisclaimerLabels;
      if (!segmentUrls[segment]) return item;
      return {
        ...item,
        spannable: labels[segment] || item.spannable,
        url: segmentUrls[segment],
      };
    }),
  };
};

/**
 * Attaches an `onPress` to each disclaimer segment from the `url` its builder tagged it with.
 *
 * Segment copy and URLs are decided by each screen's builder, so this stays free of
 * translation keys — matching on translated text is what used to break in non-English locales.
 */
export const addDisclaimerHandlers = (
  disclaimer: DisclaimerProps,
  openUrl?: OpenUrl,
): TextViewProps => {
  // Prefer the super app opener (openModule via useOpenUrl) when the caller
  // provides one; otherwise fall back to the in-app browser as before.
  const openLink: OpenUrl =
    openUrl ??
    ((url) => {
      if (url) openInAppBrowser(url);
    });

  return {
    ...disclaimer,
    // `url` is ours, not part of the Phoenix contract — drop it before rendering.
    spannableTextsProps: disclaimer.spannableTextsProps?.map(
      ({ url, ...item }) => ({
        ...item,
        onPress: url ? () => openLink(url) : item.onPress,
      }),
    ),
  };
};

// Mobile number validation constants
const MOBILE_NUMBER_MIN_LENGTH = 8;
const MOBILE_NUMBER_MAX_LENGTH = 15;
const MOBILE_NUMBER_PATTERN = /^[0-9]+$/;

// TODO Validation functions will be change based on API.
export const validateMobileNumber = (value: string, t: TFunction): string => {
  if (isEmpty(value)) {
    return t
      ? t('signUpFormView.mobileNumberRequired')
      : 'This field is required';
  }

  const cleanValue = value.replace(/\s/g, ''); // Remove spaces

  if (!MOBILE_NUMBER_PATTERN.test(cleanValue)) {
    return t
      ? t('signUpFormView.mobileNumberNumbersOnly')
      : 'Please enter numbers only';
  }

  if (cleanValue.length < MOBILE_NUMBER_MIN_LENGTH) {
    return t
      ? t('signUpFormView.mobileNumberMinLength', {
          min: MOBILE_NUMBER_MIN_LENGTH,
        })
      : `Mobile number must be at least ${MOBILE_NUMBER_MIN_LENGTH} digits`;
  }

  if (cleanValue.length > MOBILE_NUMBER_MAX_LENGTH) {
    return t
      ? t('signUpFormView.mobileNumberMaxLength', {
          max: MOBILE_NUMBER_MAX_LENGTH,
        })
      : `Mobile number must not exceed ${MOBILE_NUMBER_MAX_LENGTH} digits`;
  }

  return '';
};

export const validateDialingCode = (value: string, t: TFunction): string => {
  return isEmpty(value)
    ? t
      ? t('signUpFormView.fieldRequired')
      : 'This field is required'
    : '';
};

export const mobileNumberError = (value: string, t: TFunction) =>
  validateMobileNumber(value, t);
